import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiLock, FiMail, FiUser, FiArrowRight, FiCheck, FiAlertTriangle } = FiIcons;

export default function Login() {
  const [mode, setMode] = useState('login'); // 'login' | 'signup' | 'magic' | 'reset'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);
  
  const { signIn, signInWithGoogle, signUp, signInWithMagicLink, resetPassword, authError, user } = useAuth();
  const navigate = useNavigate();

  // If user is successfully logged in and validated, redirect to dashboard
  useEffect(() => {
    if (user && !authError) {
        navigate('/dashboard');
    }
  }, [user, authError, navigate]);

  // Handle Global Auth Errors (like "Access Denied")
  useEffect(() => {
    if (authError) {
        setMessage({ type: 'error', text: authError });
    }
  }, [authError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });
    setLoading(true);

    try {
      if (mode === 'login') {
        const { error } = await signIn(email, password);
        if (error) throw error;
        // Navigation happens in useEffect if successful
      } 
      else if (mode === 'signup') {
        const { error } = await signUp(email, password, fullName);
        if (error) throw error;
        setMessage({ type: 'success', text: 'Registrierung erfolgreich! Bitte bestätigen Sie Ihre Email (falls nötig) und warten Sie auf den Login.' });
        setMode('login');
      }
      else if (mode === 'magic') {
        const { error } = await signInWithMagicLink(email);
        if (error) throw error;
        setMessage({ type: 'success', text: 'Magic Link gesendet! Bitte prüfen Sie Ihre Email.' });
      }
      else if (mode === 'reset') {
        const { error } = await resetPassword(email);
        if (error) throw error;
        setMessage({ type: 'success', text: 'Anweisungen zum Zurücksetzen wurden gesendet.' });
      }
    } catch (error) {
      // Improve error messages
      let msg = error.message;
      if (msg === 'Invalid login credentials') msg = 'Email oder Passwort falsch.';
      setMessage({ type: 'error', text: msg });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
      setMessage({ type: '', text: '' });
      try {
          const { error } = await signInWithGoogle();
          if (error) throw error;
      } catch (error) {
          setMessage({ type: 'error', text: 'Google Login fehlgeschlagen: ' + error.message });
      }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        className="max-w-md w-full bg-white rounded-xl shadow-xl overflow-hidden"
      >
        <div className="bg-blue-600 p-8 text-center relative overflow-hidden">
          <div className="relative z-10">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 backdrop-blur-sm">
              <SafeIcon icon={mode === 'magic' ? FiMail : FiLock} className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white">Familienplaner</h2>
            <p className="text-blue-100 mt-2 text-sm">
              {mode === 'login' && 'Willkommen zurück!'}
              {mode === 'signup' && 'Konto erstellen'}
              {mode === 'magic' && 'Login ohne Passwort'}
              {mode === 'reset' && 'Passwort vergessen'}
            </p>
          </div>
        </div>

        <div className="p-8">
          
          {/* Google Login Button */}
          {mode === 'login' && (
            <div className="mb-6">
                <button 
                    onClick={handleGoogleLogin}
                    type="button"
                    className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                    <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    Mit Google anmelden
                </button>
                <div className="relative mt-6">
                    <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-200"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                        <span className="px-2 bg-white text-gray-500">Oder mit Email</span>
                    </div>
                </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {message.text && (
              <div className={`p-3 border rounded-lg text-sm flex items-start ${message.type === 'error' ? 'bg-red-50 border-red-200 text-red-600' : 'bg-green-50 border-green-200 text-green-700'}`}>
                <SafeIcon icon={message.type === 'success' ? FiCheck : FiAlertTriangle} className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                <span>{message.text}</span>
              </div>
            )}

            {mode === 'signup' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <div className="relative">
                  <SafeIcon icon={FiUser} className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                  <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="Ihr Name" required />
                </div>
                <p className="text-xs text-blue-600 mt-1">Hinweis: Nur vom Admin eingeladene Emails werden akzeptiert.</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <div className="relative">
                <SafeIcon icon={FiMail} className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="ihre@email.de" required />
              </div>
            </div>

            {mode !== 'magic' && mode !== 'reset' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Passwort</label>
                <div className="relative">
                  <SafeIcon icon={FiLock} className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                  <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="••••••••" required />
                </div>
                {mode === 'login' && (
                   <div className="flex justify-end mt-1">
                       <button type="button" onClick={() => setMode('reset')} className="text-xs text-blue-600 hover:underline">Vergessen?</button>
                   </div>
                )}
              </div>
            )}

            <button 
                type="submit" 
                disabled={loading} 
                className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center justify-center"
            >
              {loading ? 'Verarbeite...' : 
               mode === 'login' ? 'Einloggen' : 
               mode === 'signup' ? 'Registrieren' : 
               mode === 'magic' ? 'Magic Link senden' : 'Link senden'}
            </button>
          </form>

          <div className="mt-6 flex flex-col space-y-3 text-center text-sm">
             {mode === 'login' && (
                 <>
                    <button onClick={() => setMode('magic')} className="text-gray-600 hover:text-gray-900 border-b border-dotted border-gray-400 pb-0.5 inline-block mx-auto">
                        Mit Magic Link einloggen (ohne Passwort)
                    </button>
                    <div className="pt-2 border-t border-gray-100">
                        <span className="text-gray-500">Noch kein Account? </span>
                        <button onClick={() => setMode('signup')} className="text-blue-600 font-medium hover:underline">Registrieren</button>
                    </div>
                 </>
             )}
             {mode !== 'login' && (
                 <button onClick={() => setMode('login')} className="text-blue-600 font-medium hover:underline">
                    Zurück zum Login
                 </button>
             )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}